import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ICrmError, IMeetingType, IMeetingTypePatch, ITeamPatch, ITeamView, JourneyKey } from "./types";

const adminUrl = `${environment.v2Url}/crm-admin`;

export type CrmTeamsKeyFunctions = {
    list: () => Promise<ITeamView[] | ICrmError>;
    create: (dto: ITeamPatch) => Promise<ITeamView | ICrmError>;
    update: (id: number, dto: ITeamPatch) => Promise<ITeamView | ICrmError>;
    remove: (id: number) => Promise<true | ICrmError>;
    setMember: (id: number, userId: number, patch: { weight?: number; journeys?: JourneyKey[] }) => Promise<ITeamView | ICrmError>;
    removeMember: (id: number, userId: number) => Promise<ITeamView | ICrmError>;
    createMeetingType: (id: number, dto: IMeetingTypePatch) => Promise<IMeetingType | ICrmError>;
    updateMeetingType: (id: number, typeId: number, dto: IMeetingTypePatch) => Promise<IMeetingType | ICrmError>;
};

/** Manager-only: team booking links. */
export const useCrmTeams = (): [CrmTeamsKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };
    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return { ok: false, status, message: err?.response?.data?.message || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.') };
    };
    const get = async <T>(path: string) => { try { const r: AxiosResponse<T> = await axios.get(`${adminUrl}${path}`, { headers }); return r.data; } catch (err: any) { return handle(err); } };
    const post = async <T>(path: string, body: unknown) => { try { const r: AxiosResponse<T> = await axios.post(`${adminUrl}${path}`, body, { headers }); return r.data; } catch (err: any) { return handle(err); } };
    const put = async <T>(path: string, body: unknown) => { try { const r: AxiosResponse<T> = await axios.put(`${adminUrl}${path}`, body, { headers }); return r.data; } catch (err: any) { return handle(err); } };
    const del = async <T>(path: string) => { try { const r: AxiosResponse<T> = await axios.delete(`${adminUrl}${path}`, { headers }); return r.data; } catch (err: any) { return handle(err); } };

    return [{
        list: () => get<ITeamView[]>('/teams'),
        create: (dto) => post<ITeamView>('/teams', dto),
        update: (id, dto) => put<ITeamView>(`/teams/${id}`, dto),
        remove: async (id) => { const r = await del<true>(`/teams/${id}`); return r === undefined ? true : r; },
        setMember: (id, userId, patch) => put<ITeamView>(`/teams/${id}/members`, { user_id: userId, ...patch }),
        removeMember: (id, userId) => del<ITeamView>(`/teams/${id}/members/${userId}`),
        createMeetingType: (id, dto) => post<IMeetingType>(`/teams/${id}/meeting-types`, dto),
        updateMeetingType: (id, typeId, dto) => put<IMeetingType>(`/teams/${id}/meeting-types/${typeId}`, dto)
    }];
};
